"use client";

import { Pagination } from "@/components/Pagination";
import { PaginationContent } from "@/components/pagination/PaginationContent";
import { PaginationItem } from "@/components/pagination/PaginationItem";
import { PaginationLink } from "@/components/pagination/PaginationLink";
import { PaginationNext } from "@/components/pagination/PaginationNext";
import { PaginationPrevious } from "@/components/pagination/PaginationPrevious";
import { NormalText } from "@/components/text/NormalText";

export function CustomPagination({
  currentPage,
  setCurrentPage,
  numberOfPages,
  containerClassName,
}: {
  currentPage: number;
  setCurrentPage: (value: number) => void;
  numberOfPages: number;
  containerClassName?: string;
}) {
  if (numberOfPages <= 1) {
    return null;
  }

  const getVisiblePages = () => {
    const delta = 2; // 1 page on each side for mobile, 2 for desktop

    // Special case for delta = pages - just show both
    if (numberOfPages === delta) {
      return Array.from({ length: numberOfPages }, (_, i) => i + 1);
    }
    const range = [];

    // Always show first page
    range.push(1);

    // If we're on first page, show next few pages
    if (currentPage === 1) {
      const maxPages = 5; // Show fewer pages on mobile
      for (
        let i = 2;
        i <= Math.min(Math.min(delta, numberOfPages), maxPages);
        i++
      ) {
        range.push(i);
      }
    } else {
      // Normal logic for other pages
      for (
        let i = Math.max(2, currentPage - delta);
        i <= Math.min(numberOfPages - 1, currentPage + delta);
        i++
      ) {
        range.push(i);
      }
    }

    // Always show last page if there are more than one page
    if (numberOfPages > 1) {
      range.push(numberOfPages);
    }

    const withEllipsis = [];
    let prev = 0;

    for (const i of range) {
      if (typeof prev === "number" && prev > 0 && i - prev > 1) {
        withEllipsis.push("...");
      }
      withEllipsis.push(i);
      prev = i;
    }

    return withEllipsis;
  };

  return (
    <Pagination className={containerClassName}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => setCurrentPage(currentPage - 1)}
            className={currentPage === 1 ? "invisible" : ""}
          />
        </PaginationItem>

        {getVisiblePages().map((pageNumber, index) => (
          <PaginationItem key={index} className="hidden sm:block">
            {pageNumber === "..." ?
              <span className="px-4">...</span>
            : <PaginationLink
                onClick={() => setCurrentPage(pageNumber as number)}
                isActive={currentPage === pageNumber}
              >
                {pageNumber}
              </PaginationLink>
            }
          </PaginationItem>
        ))}

        {/* Mobile current page indicator */}
        <PaginationItem className="sm:hidden">
          <NormalText elementType="span">
            {currentPage} / {numberOfPages}
          </NormalText>
        </PaginationItem>

        <PaginationItem>
          <PaginationNext
            onClick={() => setCurrentPage(currentPage + 1)}
            className={
              currentPage === numberOfPages || numberOfPages === 0 ?
                "invisible"
              : ""
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
